import type { LocalDate } from '@js-joda/core';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { QueryObserver } from '@tanstack/react-query';
import type { QueryClient, QueryObserverResult } from '@tanstack/react-query';
import type {
    BacktestUniverseSize,
    BacktestUniverseSizeRequest,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import type { Idea, ScreenerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import debounce from 'lodash.debounce';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { UniverseCreationModel } from 'src/models/UniverseCreationModel/UniverseCreationModel';
import { getBacktestUniverseSizeQuery } from 'src/queries/backtestSpecs';
import type { FormulaService } from 'src/services/FormulaService';
import type { ReactBindings } from 'src/types/bindings';

type UniverseCreationModelInstance = InstanceType<typeof UniverseCreationModel>;

export type UniverseSizeStatus = 'idle' | 'pending' | 'error';

interface UniverseStepReducerState {
    readonly creationModel: UniverseCreationModelInstance;
    readonly lastSavedJson: string;
    readonly universeSize: BacktestUniverseSize | null;
    readonly universeSizeStatus: UniverseSizeStatus;
}

type UniverseStepAction =
    | { type: 'HYDRATE'; plan: ScreenerPlan }
    | { type: 'CLEAR' }
    | { type: 'MARK_SAVED' }
    | { type: 'SET_UNIVERSE_SIZE'; result: QueryObserverResult<BacktestUniverseSize | null, Error> };

const EMPTY_PLAN_JSON = JSON.stringify({
    type: 'screener',
    mode: 'include-all',
    conditions: [],
    includeList: [],
    excludeList: [],
} satisfies ScreenerPlan);

function snapshotJson(creationModel: UniverseCreationModelInstance): string {
    return JSON.stringify(creationModel.idea.peek().plan);
}

function statusFromResult(result: QueryObserverResult<BacktestUniverseSize | null, Error>): UniverseSizeStatus {
    if (result.isFetching) return 'pending';
    if (result.isError) return 'error';
    return 'idle';
}

function reducer(state: UniverseStepReducerState, action: UniverseStepAction): UniverseStepReducerState {
    switch (action.type) {
        case 'HYDRATE': {
            state.creationModel.hydrate(action.plan);
            return { ...state, lastSavedJson: snapshotJson(state.creationModel) };
        }
        case 'CLEAR': {
            state.creationModel.clear();
            if (state.lastSavedJson === EMPTY_PLAN_JSON) return state;
            return { ...state, lastSavedJson: EMPTY_PLAN_JSON };
        }
        case 'MARK_SAVED': {
            const next = snapshotJson(state.creationModel);
            if (state.lastSavedJson === next) return state;
            return { ...state, lastSavedJson: next };
        }
        case 'SET_UNIVERSE_SIZE': {
            return {
                ...state,
                universeSize: action.result.data ?? null,
                universeSizeStatus: statusFromResult(action.result),
            };
        }
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Universe pane signal model. Owns a `UniverseCreationModel` (screener-style
 * condition builder) and surfaces the plan it produces as the universe's
 * `Idea` for the BacktestSpec. Wraps UCM with dirty tracking and the
 * hydrate/markSaved/reset lifecycle the rest of the form expects.
 *
 * Also drives a debounced preview of the universe size — fired against
 * `POST /backtests/universe-size` whenever the in-progress plan or date range
 * changes — and exposes the result through `universeSize` / `universeSizeStatus`.
 */
export class UniverseStepModel implements Disposable {
    readonly #state: Signal<UniverseStepReducerState>;
    readonly #logger: ReactBindings['Logger'];
    readonly #screenerPlan: ReadonlySignal<ScreenerPlan>;
    readonly #universeSizeRequest: ReadonlySignal<BacktestUniverseSizeRequest | null>;
    readonly #disposeEffects: Array<() => void> = [];
    readonly #applyOptions: ReturnType<typeof debounce<(req: BacktestUniverseSizeRequest | null) => void>>;

    readonly creationModel: UniverseCreationModelInstance;
    readonly isDirty: ReadonlySignal<boolean>;
    readonly errors: ReadonlySignal<readonly string[]>;
    readonly isValid: ReadonlySignal<boolean>;
    readonly universeSize: ReadonlySignal<BacktestUniverseSize | null>;
    readonly universeSizeStatus: ReadonlySignal<UniverseSizeStatus>;

    constructor(
        queryClient: QueryClient,
        logger: ReactBindings['Logger'],
        startDate: ReadonlySignal<LocalDate | null>,
        endDate: ReadonlySignal<LocalDate | null>,
        formulaService: FormulaService,
    ) {
        this.#logger = logger;
        const creationModel = new UniverseCreationModel({ searchModel: 'backtest', queryClient, formulaService });
        this.#state = signal<UniverseStepReducerState>({
            creationModel,
            lastSavedJson: EMPTY_PLAN_JSON,
            universeSize: null,
            universeSizeStatus: 'idle',
        });

        this.creationModel = creationModel;
        this.#screenerPlan = computed(() => creationModel.idea.value.plan as ScreenerPlan);
        this.isDirty = computed(() => JSON.stringify(this.#screenerPlan.value) !== this.#state.value.lastSavedJson);
        this.universeSize = computed(() => this.#state.value.universeSize);
        this.universeSizeStatus = computed(() => this.#state.value.universeSizeStatus);
        // Only surface the data-level "no symbols" error once the query has
        // settled; while pending, complexity.isBlocking already gates canRun
        // and surfacing mid-load would flicker the error in and out.
        this.errors = computed(() => {
            const base = creationModel.errors.value;
            const size = this.#state.value.universeSize;
            const status = this.#state.value.universeSizeStatus;
            if (status === 'idle' && size != null && size.totalUniqueSecurities === 0) {
                return [...base, 'Universe matches no symbols — adjust criteria or include list'];
            }
            return base;
        });
        this.isValid = computed(() => this.errors.value.length === 0);

        // Recompute the request body whenever the in-progress plan or date range
        // changes. Returns null to skip the call when we can't or shouldn't ask.
        this.#universeSizeRequest = computed<BacktestUniverseSizeRequest | null>(() => {
            const start = startDate.value;
            const end = endDate.value;
            if (start == null || end == null) return null;
            const idea = creationModel.idea.value;
            return { universePlan: idea, startDate: start, endDate: end };
        });

        const observer = new QueryObserver<BacktestUniverseSize | null, Error>(
            queryClient,
            getBacktestUniverseSizeQuery(untracked(() => this.#universeSizeRequest.value)),
        );

        this.#applyOptions = debounce((req: BacktestUniverseSizeRequest | null) => {
            observer.setOptions(getBacktestUniverseSizeQuery(req));
        }, 500);

        this.#disposeEffects.push(
            effect(() => {
                this.#applyOptions(this.#universeSizeRequest.value);
            }),
        );

        this.#disposeEffects.push(
            effect(() => {
                const unsubscribe = observer.subscribe((result) => {
                    this.#dispatch({ type: 'SET_UNIVERSE_SIZE', result });
                });
                this.#dispatch({ type: 'SET_UNIVERSE_SIZE', result: observer.getCurrentResult() });
                return () => {
                    unsubscribe();
                    observer.destroy();
                };
            }),
        );
    }

    [Symbol.dispose](): void {
        this.#applyOptions.cancel();
        for (const dispose of this.#disposeEffects) dispose();
        this.#disposeEffects.length = 0;
        this.creationModel[Symbol.dispose]();
    }

    #dispatch(action: UniverseStepAction): void {
        // Read via untracked so reducer dispatches fired from inside effects (the
        // QueryObserver subscription, in particular) don't form a read/write
        // cycle on `#state`.
        this.#state.value = reducer(
            untracked(() => this.#state.value),
            action,
        );
    }

    /**
     * Hydrate from a saved BacktestSpec.universePlan. The new BacktestSpec
     * encodes the universe as `Idea | uuid`. We support inlined Ideas with a
     * ScreenerPlan; uuid pointers and other plan kinds are logged and treated
     * as empty until the broader Idea-fetch flow lands.
     */
    hydrate(idea: Idea | string | null): void {
        if (idea == null) {
            this.#dispatch({ type: 'CLEAR' });
            return;
        }
        if (typeof idea === 'string') {
            this.#logger.warn('UniverseStepModel.hydrate received a uuid universePlan; not yet supported', {
                uuid: idea,
            });
            this.#dispatch({ type: 'CLEAR' });
            return;
        }
        if (idea.plan.type !== 'screener') {
            this.#logger.warn('UniverseStepModel.hydrate received a non-screener plan; not yet supported', {
                planType: idea.plan.type,
            });
            this.#dispatch({ type: 'CLEAR' });
            return;
        }
        this.#dispatch({ type: 'HYDRATE', plan: idea.plan });
    }

    markSaved(): void {
        this.#dispatch({ type: 'MARK_SAVED' });
    }

    reset(): void {
        this.#dispatch({ type: 'CLEAR' });
    }
}
